import { useBookingContext } from '../../../providers/BookingFormProvider/BookingFormProvider'
import { PaymentSelector } from '../../../components/PaymentSelector/PaymentSelector'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormNotice } from '../../../components/FormNotice/FormNotice'
import { __ } from '@wordpress/i18n'
import './Steps.scss'
import { Button } from '../../../components/Button/Button'
import { useDispatch } from '@wordpress/data'
import { store_name } from '../../../../store/frontend'
import sslPaymentLogo from '../../../../../public/images/logos/ssl-encryption.svg'
import protectedPrivacyLogo from '../../../../../public/images/logos/privacy-protected.svg'
import { CustomScroll } from 'react-custom-scroll'
import classNames from 'classnames'
import { useWording } from '../../../hooks/useWording'

export const PaymentStep = () => {
    const { fetchBookingAmounts } = useDispatch(store_name)
    const { formData, setFormData, paymentMethods, amountData, preset } =
        useBookingContext()

    const couponInputRef = useRef<HTMLInputElement>(null)
    const [applied, setApplied] = useState(false)
    const [showFeedback, setShowFeedback] = useState(false)

    const handleSelectMethod = useCallback(
        (method: string) => {
            setFormData('payment_method', method)
        },
        [setFormData]
    )

    const applyCoupon = useCallback(() => {
        const coupon = couponInputRef.current?.value || ''
        const updatedFormData = {
            ...formData,
            coupon,
        }

        if (
            !coupon ||
            !updatedFormData.places ||
            Object.keys(updatedFormData.places).length === 0
        ) {
            return
        }

        fetchBookingAmounts(updatedFormData)
        setApplied(true)
        setShowFeedback(true)
    }, [formData, fetchBookingAmounts])

    // Reset feedback when coupon changes
    useEffect(() => {
        setApplied(false)
        setShowFeedback(false)
    }, [formData.coupon])

    const couponApplied = applied && amountData.discount > 0
    const couponFailed = applied && showFeedback && amountData.discount === 0
    const wording = useWording()

    const paymentMessage = useMemo(() => {
        if (formData.payment_method === 'bank') {
            return wording?.bank_transfer_message || ''
        }
        if (formData.payment_method === 'arrival') {
            return wording?.pay_on_arrival_message || ''
        }
        if (formData.payment_method === 'paypal') {
            return wording?.paypal_message || ''
        }
        if (formData.payment_method === 'woocommerce') {
            return wording?.woocommerce_message || ''
        }
        return ''
    }, [wording, formData.payment_method])

    return (
        <CustomScroll flex="1" className={'wbk_step__scroll-wrapper'}>
            <div className={'wbk_step__payment-step'}>
                <PaymentSelector
                    selectedMethod={formData.payment_method as string}
                    setSelectedMethod={handleSelectMethod}
                    methods={paymentMethods}
                />

                {['bank', 'arrival', 'woocommerce', 'paypal'].includes(
                    formData.payment_method
                ) && <FormNotice notice={paymentMessage} />}

                <div className={'wbk_step__separator'}></div>

                {preset?.settings.coupons_enabled && (
                    <>
                        <div className={'wbk_coupon__wrapper'}>
                            <input
                                type="text"
                                placeholder={
                                    wording?.enter_coupon_code ||
                                    __(
                                        'Enter coupon code',
                                        'webba-booking-lite'
                                    )
                                }
                                name="coupon"
                                id="coupon"
                                ref={couponInputRef}
                                value={formData.coupon || ''}
                                onChange={(e) => {
                                    setFormData('coupon', e.target.value)
                                }}
                            />
                            <Button
                                onClick={applyCoupon}
                                type="secondary"
                                disabled={applied && amountData.discount > 0}
                            >
                                {applied && amountData.discount > 0
                                    ? wording?.applied ||
                                      __('Applied', 'webba-booking-lite')
                                    : wording?.apply ||
                                      __('Apply', 'webba-booking-lite')}
                            </Button>
                        </div>
                        {couponApplied && (
                            <div
                                className={classNames(
                                    'wbk_coupon__feedback',
                                    'wbk_coupon__feedback--success'
                                )}
                            >
                                {wording?.coupon_applied_success ||
                                    __(
                                        'Coupon applied successfully!',
                                        'webba-booking-lite'
                                    )}
                            </div>
                        )}
                        {couponFailed && (
                            <div
                                className={classNames(
                                    'wbk_coupon__feedback',
                                    'wbk_coupon__feedback--failed'
                                )}
                            >
                                {wording?.coupon_failed ||
                                    __(
                                        'Coupon did not apply. Please check your code.',
                                        'webba-booking-lite'
                                    )}
                            </div>
                        )}
                    </>
                )}

                <div className={'wbk_step__certificates'}>
                    <img
                        src={sslPaymentLogo}
                        alt={wording?.ssl || __('SSL', 'webba-booking-lite')}
                    />
                    <img
                        src={protectedPrivacyLogo}
                        alt={
                            wording?.privacy ||
                            __('Privacy', 'webba-booking-lite')
                        }
                    />
                </div>
            </div>
        </CustomScroll>
    )
}
